import React from 'react';

export type GuideAxis = 'x' | 'y';

export const guideKey = (axis: GuideAxis, value: number) => `${axis}:${value.toFixed(2)}`;

export const parseGuideKey = (key: string): { axis: GuideAxis; value: number } | null => {
  const sep = key.indexOf(':');
  if (sep < 0) return null;
  const axis = key.slice(0, sep);
  const value = Number(key.slice(sep + 1));
  if ((axis !== 'x' && axis !== 'y') || !Number.isFinite(value)) return null;
  return { axis, value };
};

const HIT = 4;
const GUIDE_THICKNESS = 0.5;
const GUIDE_THICKNESS_SELECTED = 1;

export const GuidesOverlay: React.FC<{
  guides: { x: number[]; y: number[] };
  selected: Set<string>;
  onToggle: (axis: GuideAxis, value: number, e: React.MouseEvent<HTMLDivElement>) => void;
  width: number;
  height: number;
  interactive?: boolean;
}> = ({ guides, selected, onToggle, width, height, interactive = true }) => {
  const handle = (axis: GuideAxis, value: number) => (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    onToggle(axis, value, e);
  };

  return (
    <div
      data-guides-overlay
      style={{
        position: 'absolute',
        left: 0,
        top: 0,
        width,
        height,
        pointerEvents: 'none',
        zIndex: 50,
      }}
    >
      {(guides.x || []).map((x) => {
        const key = guideKey('x', x);
        const isSel = selected.has(key);
        const thick = isSel ? GUIDE_THICKNESS_SELECTED : GUIDE_THICKNESS;
        // Browsers often snap very small widths (<~0.5px) to device pixels.
        // Use transform scaling on a 1px element to reliably show "hairline" guides.
        const sx = Math.max(0.05, thick);
        return (
          <div
            key={key}
            data-guide="x"
            title="Select guide — Shift/Ctrl+click for more, Delete to remove"
            onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
            onClick={interactive ? handle('x', x) : undefined}
            style={{
              position: 'absolute',
              left: x - HIT / 2,
              top: 0,
              width: HIT,
              height,
              pointerEvents: interactive ? 'auto' : 'none',
              cursor: interactive ? 'col-resize' : 'default',
              boxSizing: 'border-box',
            }}
          >
            <div
              style={{
                position: 'absolute',
                left: (HIT - 1) / 2,
                top: 0,
                width: 1,
                height: '100%',
                transform: `scaleX(${sx})`,
                transformOrigin: 'center',
                backgroundColor: isSel ? 'rgba(245, 34, 45, 0.95)' : 'rgba(0, 128, 255, 0.75)',
              }}
            />
          </div>
        );
      })}
      {(guides.y || []).map((y) => {
        const key = guideKey('y', y);
        const isSel = selected.has(key);
        const thick = isSel ? GUIDE_THICKNESS_SELECTED : GUIDE_THICKNESS;
        const sy = Math.max(0.05, thick);
        return (
          <div
            key={key}
            data-guide="y"
            title="Select guide — Shift/Ctrl+click for more, Delete to remove"
            onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
            onClick={interactive ? handle('y', y) : undefined}
            style={{
              position: 'absolute',
              left: 0,
              top: y - HIT / 2,
              width,
              height: HIT,
              pointerEvents: interactive ? 'auto' : 'none',
              cursor: interactive ? 'row-resize' : 'default',
              boxSizing: 'border-box',
            }}
          >
            <div
              style={{
                position: 'absolute',
                left: 0,
                top: (HIT - 1) / 2,
                width: '100%',
                height: 1,
                transform: `scaleY(${sy})`,
                transformOrigin: 'center',
                backgroundColor: isSel ? 'rgba(245, 34, 45, 0.95)' : 'rgba(0, 128, 255, 0.75)',
              }}
            />
          </div>
        );
      })}
    </div>
  );
};

export default GuidesOverlay;
